import { debounce } from 'lodash'
import b64toBlob from 'b64-to-blob'

import { fileUploadInTopics } from '../../../../components/utils/file-upload/fileUploadInTopics'
import { LooopS3FileUpload } from '../../../../app/util/LooopS3FileUpload'
import striptags from 'striptags'

import {
  LooopS3BucketEnum,
  TopicElementModelAsEditor
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

/**
 * @deprecated do not use in components, prefer useDebouncedCallback from use-debounce because it has very handy https://github.com/xnimorz/use-debounce#callpending-method
 */
export const debounced = debounce((func: () => any) => {
  return func()
}, 500)

const BASE64_STRING = 'data:image/([a-zA-Z]*);base64,([^"]*)'
const BASE64G = new RegExp(BASE64_STRING, 'g')
const BASE64 = new RegExp(BASE64_STRING)

export const replaceBase64ImagesInHtml = async (
  html: string,
  imageProcessing: {
    S3path: string
    bucket: LooopS3BucketEnum
  },
  uploadProgress?: (percentCompleted: number) => void,
  uploadFn = LooopS3FileUpload,
  b64converter = b64toBlob
): Promise<string> => {
  let content = html

  const matches = html.match(BASE64G)

  if (!matches) {
    return content
  }

  const promises = matches.map(
    async (
      rawMatch
    ): Promise<{
      base64string: string
      url: string
    }> => {
      const [regexpMatch, contentType, b64Data] = rawMatch.match(
        BASE64
      ) as string[] // there is always going to be a match
      const response = await uploadFn({
        S3UploadProps: {
          filename: `${+new Date()}.${contentType}`,
          S3path: imageProcessing.S3path,
          bucket: imageProcessing.bucket
        },
        blob: b64converter(b64Data, contentType),
        on: {
          uploadProgress
        }
      })

      if (!response) {
        throw new Error("Image couldn't be processed")
      }

      return {
        base64string: regexpMatch,
        url: response
      }
    }
  )

  const images = await Promise.all(promises)

  images.forEach((i) => {
    content = content.replace(i.base64string, i.url)
  })

  return content
}

export const patchElementContent = async (
  element: TopicElementModelAsEditor,
  topic: { id: number },
  html: string,
  uploadProgress: (percentCompleted: number) => void,
  uploadFn = fileUploadInTopics,
  b64converter = b64toBlob
): Promise<string> => {
  let content = html

  const matches = html.match(BASE64G)

  if (!matches || !topic) {
    return content
  }

  const promises = matches.map(async (rawMatch) => {
    const [regexpMatch, contentType, b64Data] = rawMatch.match(
      BASE64
    ) as string[] // there is always going to be a match
    const url = await uploadFn({
      topicId: topic.id,
      elementType: element.type,
      elementId: element.id,
      blob: b64converter(b64Data, contentType),
      fileName: `${+new Date()}.${contentType}`,
      documentType: 'image',
      on: {
        uploadProgress
      }
    })

    return {
      base64string: regexpMatch,
      url
    }
  })

  const images = await Promise.all(promises)

  images.forEach((i) => {
    content = content.replace(i.base64string, i.url)
  })

  return content
}

export const elementShouldStayInEditingMode = (
  isMultiPage: boolean,
  input: string
) => {
  const allowedTags = ['img']

  return isMultiPage || !input || striptags(input, allowedTags).trim() === ''
}
